import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Sun } from "lucide-react";

export const Login = () => {
  const { login, t } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#F9F8F6] flex items-center justify-center px-6 py-12 grain-bg">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.jpg" alt="SolarControl Minniti" className="h-14 w-14 object-contain rounded-full bg-white" />
          <span className="text-2xl font-black"><span className="text-[#EAB308]">Solar</span><span className="text-[#17301C]">Control</span> <span className="text-sm font-black tracking-widest text-[#17301C] uppercase">Minniti</span></span>
        </div>
        <h1 className="text-4xl font-black text-[#17301C] tracking-tight mb-2">{t.auth.login_title}</h1>
        <p className="text-[#3A4A3E] mb-8">Bentornato tra i pionieri dell&apos;energia solare.</p>
        <form onSubmit={submit} className="space-y-4 card-surface p-8">
          <div>
            <label className="label-eco block mb-2">{t.auth.email}</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]" />
          </div>
          <div>
            <label className="label-eco block mb-2">{t.auth.password}</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]" />
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full mt-4 disabled:opacity-60">
            {loading ? "..." : t.auth.submit_login}
          </button>
          <Link to="/register" data-testid="link-to-register" className="block text-center text-sm text-[#17301C] hover:underline mt-4">{t.auth.switch_to_register}</Link>
        </form>
      </div>
    </div>
  );
};

export const Register = () => {
  const { register, t } = useApp();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, referralCode || undefined);
      toast.success(referralCode ? "Account creato + 1 mese Pro gratis!" : "Account creato! Configuriamo il tuo impianto.");
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#F9F8F6] flex items-center justify-center px-6 py-12 grain-bg">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.jpg" alt="SolarControl Minniti" className="h-14 w-14 object-contain rounded-full bg-white" />
          <span className="text-2xl font-black"><span className="text-[#EAB308]">Solar</span><span className="text-[#17301C]">Control</span> <span className="text-sm font-black tracking-widest text-[#17301C] uppercase">Minniti</span></span>
        </div>
        <h1 className="text-4xl font-black text-[#17301C] tracking-tight mb-2">{t.auth.register_title}</h1>
        <p className="text-[#3A4A3E] mb-8">Inizia il viaggio verso l&apos;autonomia energetica.</p>
        <form onSubmit={submit} className="space-y-4 card-surface p-8">
          <div>
            <label className="label-eco block mb-2">{t.auth.name}</label>
            <input data-testid="register-name" type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]" />
          </div>
          <div>
            <label className="label-eco block mb-2">{t.auth.email}</label>
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]" />
          </div>
          <div>
            <label className="label-eco block mb-2">{t.auth.password}</label>
            <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]" />
          </div>
          <div>
            <label className="label-eco block mb-2">Codice referral (opzionale)</label>
            <input data-testid="register-referral" placeholder="ABC12345" value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 rounded-lg border border-[#DDE0D8] focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C] font-mono uppercase" />
            {referralCode && <div className="text-xs text-[#166534] mt-1">🎁 1 mese Pro gratis con questo codice</div>}
          </div>
          <button data-testid="register-submit" type="submit" disabled={loading} className="btn-primary w-full mt-4 disabled:opacity-60">
            {loading ? "..." : t.auth.submit_register}
          </button>
          <Link to="/login" data-testid="link-to-login" className="block text-center text-sm text-[#17301C] hover:underline mt-4">{t.auth.switch_to_login}</Link>
        </form>
      </div>
    </div>
  );
};
